import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _class, _AbListBox;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbListMachine } from "abreact/incubator/ui/component/AbListMachine";
import { BaseRCmp } from "abreact/incubator/ui/component/BaseRCmp";
import { defaultItemDisplayStringFunction } from "abreact/incubator/ui/form/AbInputUtils";
import { SelectionManager, SelectionManagerValuePicker } from "abreact/incubator/ui/manager/SelectionManager";
import { AbValuePicker } from "abreact/incubator/ui/modal/AbValuePicker";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { observer } from "mobx-react";
import * as PropTypes from "prop-types";
import * as React from "react";
import { SEPARATOR } from "abreact/input/AbList";
import { DISABLE_DEPRECATION, getDeprecatedImageStyle, getDeprecatedTooltip } from "abreact/misc/AbDeprecatedIndicator";
import { AbLink } from "abreact/misc/AbLink";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";

/**
 * The type for item callbacks.
 */

/**
 * Properties for an item renderer.
 */

/**
 * A generic renderer for an item.
 */

/**
 * The properties to be specified when creating an AbListBox.
 *
 * @deprecated in favor of AbListProps from abreact/input/AbList.
 */

/**
 * The properties to be specified when creating a modal containing a list box.
 *
 * @deprecated in favor of abreact/input/AbList which supports virtualized lists.
 */

/**
 * A component that lets the user choose from a list of items.
 *
 * @deprecated in favor of abreact/input/AbList which supports virtualized lists.
 */
export let AbListBox = observer(_class = (_AbListBox = class AbListBox extends BaseRCmp {
  /**
   * The default renderer that can be used for any item.
   *
   * @param {ItemRendererProps<ItemT>} props The properties to configure the render.
   * @returns {React.ReactNode} The rendered node for the item.
   */
  static defaultItemRenderer(props) {
    const {
      id,
      className,
      style,
      selected,
      disabled,
      item,
      itemDisplayStringFunction,
      onItemClick,
      onItemDoubleClick
    } = props;
    const classes = ReactUtils.classNamesString([className, selected ? "selected" : undefined, disabled ? "ab-disabled" : undefined]);
    const label = itemDisplayStringFunction(item);
    const onClick = onItemClick ? () => onItemClick(item) : undefined;
    const onDoubleClick = onItemDoubleClick ? () => onItemDoubleClick(item) : undefined;
    return /*#__PURE__*/React.createElement("div", {
      id: id,
      className: classes,
      style: style,
      onClick: onClick,
      onDoubleClick: onDoubleClick
    }, label);
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "_listMachine", void 0);
    const listMachine = props.listMachine || this.createDefaultListMachine(props);
    this._listMachine = listMachine;
    this.listMachine.items = props.items;
    const onChange = this.props.onChange;
    if (onChange) {
      this.registerDestroyListener(listMachine.selectionManager.observeSelectedItems(() => onChange(this.value)));
    }
    this.updateSelectionManager(props, true);
  }
  componentWillReceiveProps(nextProps, nextContent) {
    // TODO: only update the items if they have changed
    this.listMachine.items = nextProps.items;
    this.updateSelectionManager(nextProps, false);
  }
  get listMachine() {
    return this._listMachine;
  }
  createDefaultListMachine(props) {
    const selectionManager = props.selectionManager || this.createDefaultSelectionManager();
    const listMachine = new AbListMachine({
      selectionManager: selectionManager,
      selectionMode: props.selectionMode,
      items: props.items,
      itemDisplayStringFunction: props.itemDisplayStringFunction
    });
    this.registerDestroyListener(listMachine);
    return listMachine;
  }
  get value() {
    if (this.selectionManager.selectionMode === SelectionMode.SINGLE) {
      return this.selectionManager.selectedItem;
    } else if (this.selectionManager.selectionMode === SelectionMode.MULTIPLE) {
      return this.selectionManager.selectedItems;
    }
    return undefined;
  }
  updateSelectionManager(props, allowInitialSelectedItems) {
    if (props.hasOwnProperty("value")) {
      if (ClassUtils.isInstanceOf(props.value, Array)) {
        this._listMachine.selectionManager.selectedItems = props.value;
      } else {
        this._listMachine.selectionManager.selectedItem = props.value;
      }
    } else if (allowInitialSelectedItems && props.initialSelectedItem) {
      this._listMachine.selectionManager.selectedItem = props.initialSelectedItem;
    } else if (allowInitialSelectedItems && props.initialSelectedItems) {
      this._listMachine.selectionManager.selectedItems = props.initialSelectedItems;
    }
  }
  get selectionManager() {
    return this.listMachine.selectionManager;
  }
  createDefaultSelectionManager() {
    return new SelectionManager({
      selectionMode: this.props.selectionMode
    });
  }
  onItemClick(item) {
    this.selectionManager && this.selectionManager.toggleSelect(item);
    this.props.onItemClick && this.props.onItemClick(item);
  }
  onItemDoubleClick(item) {
    this.selectionManager && this.selectionManager.toggleSelect(item);
    this.props.onItemDoubleClick && this.props.onItemDoubleClick(item);
  }
  onKeyDown(event) {
    if (this.listMachine.onKeyDown(event.key)) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
  onKeyPress(event) {
    if (this.listMachine.onKeyPress(event.key)) {
      event.stopPropagation();
    }
  }

  /**
   * Choose an item from a modal list box.
   *
   * @param {AbListBoxProps<ItemT>} props Properties for the list box.
   * @returns {Promise<ItemT>}
   */
  static chooseItem(props) {
    const selectionManager = new SelectionManager({
      selectionMode: SelectionMode.SINGLE
    });
    if (props.initialSelectedItems) {
      selectionManager.selectedItems = props.initialSelectedItems;
    } else if (props.initialSelectedItem) {
      selectionManager.selectedItem = props.initialSelectedItem;
    }
    const valuePicker = new SelectionManagerValuePicker(selectionManager);
    const listMachine = new AbListMachine({
      selectionManager: selectionManager
    });
    return new AbValuePicker().pickValue(valuePicker, {
      view: /*#__PURE__*/React.createElement(AbListBox, {
        items: props.items,
        listMachine: listMachine
      }),
      onKeyDown: event => listMachine.onKeyDown(event.key),
      onKeyPress: event => listMachine.onKeyPress(event.key),
      contentStyle: {
        padding: 0
      },
      resizable: false,
      // Note: this can be overridden via the "modalProperties" property
      ...props.popupProperties
    });
  }
  static itemKey(item, index) {
    // TODO: is there a better algorithm for determining a unique key for an arbitrary item?
    if (item === undefined) {
      return `ab-item-undefined`;
    }
    if (item === null) {
      return `ab-item-null`;
    }
    if (typeof item === "object") {
      if ("id" in item) {
        return item.id;
      }
      if ("Name" in item) {
        return item.Name;
      }
    }
    return `ab-item-${index}`;
  }
  render() {
    const deprecationClassName = DISABLE_DEPRECATION ? undefined : "ab-deprecated-1";
    const deprecationStyle = getDeprecatedImageStyle();
    const deprecationTooltip = getDeprecatedTooltip(this.props.tooltip, {
      currentPath: "webcommons/abreact/client/webapp/abreact/incubator/ui/component/AbListBox.tsx",
      newPath: "webcommons/abreact/client/webapp/abreact/input/AbList.tsx",
      contact: "@soliphant",
      /* eslint-disable i18next/no-literal-string -- deprecation migration notice */
      migrationHelp: /*#__PURE__*/React.createElement(React.Fragment, null, "See the", " ", /*#__PURE__*/React.createElement(AbLink, {
        label: "LiteBrite list sampler",
        href: "https://absdk.abinitio.com/litebrite/spa/#/sampler/lists"
      }), " ", "for examples of how to use the new list.")
      /* eslint-enable i18next/no-literal-string */
    });
    const selectionManager = this.selectionManager;
    const itemRenderer = this.props.itemRenderer || AbListBox.defaultItemRenderer;
    const itemDisplayStringFunction = this.props.itemDisplayStringFunction || defaultItemDisplayStringFunction;
    return /*#__PURE__*/React.createElement(AbWithTooltip, {
      tooltip: deprecationTooltip
    }, /*#__PURE__*/React.createElement("ul", _extends({}, ReactUtils.inputProps("ab-list-box", this.props, [this.props.disabled && "disabled", deprecationClassName]), {
      style: {
        ...this.props.style,
        ...deprecationStyle
      },
      role: "listbox",
      "aria-multiselectable": "true",
      tabIndex: !this.props.disabled ? this.props.tabIndex || 0 : undefined,
      onKeyDown: !this.props.disabled ? this.onKeyDown.bind(this) : undefined,
      onKeyPress: !this.props.disabled && !this.props.disableTypeToSearch ? this.onKeyPress.bind(this) : undefined
    }), this.props.items.map(((item, index) => {
      const key = AbListBox.itemKey(item, index);
      let showSelected = false;
      if (selectionManager) {
        if (item === undefined) {
          // Show the undefined item as selected if there is no selected item
          showSelected = selectionManager.selectedItem === undefined;
        } else {
          showSelected = selectionManager.isSelected(item);
        }
      }
      return /*#__PURE__*/React.createElement(AbListBoxItem, {
        key: key,
        itemRenderer: itemRenderer,
        item: item,
        itemDisplayStringFunction: itemDisplayStringFunction,
        selected: showSelected,
        disabled: this.props.disabled || (item !== undefined && item != null && item.hasOwnProperty("disabled") ? Boolean(item.disabled) : false),
        notMeantForSelection: this.props.selectionMode === SelectionMode.NONE,
        onItemClick: this.onItemClick.bind(this),
        onItemDoubleClick: this.onItemDoubleClick.bind(this)
      });
    }).bind(this))));
  }
}, _defineProperty(_AbListBox, "contextTypes", {
  selectionManager: PropTypes.any
}), _AbListBox)) || _class;
class AbListBoxItem extends BaseRCmp {
  ensureScrolledToView(el) {
    const container = el.parentElement;
    if (!container) {
      return;
    }

    // Get the absolute positions of the element and the container
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Note that the use of Math.ceil is to compensate for browsers using floating point numbers for positions. Firefox
    // and edge weren't scrolling quite enough.

    if (elRect.top < containerRect.top) {
      // Element is off the top of the container. Scroll up enough to show its top
      container.scrollTop -= Math.ceil(containerRect.top - elRect.top);
    } else if (elRect.bottom > containerRect.bottom) {
      // Element is off the bottom. Scroll down enough to show its bottom
      container.scrollTop += Math.ceil(elRect.bottom - containerRect.bottom);
    }
  }
  render() {
    const {
      item,
      itemRenderer,
      itemDisplayStringFunction,
      onItemClick,
      onItemDoubleClick,
      selected,
      disabled,
      notMeantForSelection
    } = this.props;
    const classes = ReactUtils.classNamesString(["ab-item", selected ? "selected" : undefined, disabled ? "ab-disabled" : undefined, notMeantForSelection ? "ab-item-no-selection" : undefined]);
    if (item === SEPARATOR) {
      return /*#__PURE__*/React.createElement("hr", {
        className: "ab-dropdown-separator"
      });
    }
    return /*#__PURE__*/React.createElement("li", {
      className: classes,
      "aria-disabled": disabled,
      "aria-selected": selected,
      onClick: onItemClick ? () => onItemClick(item) : undefined,
      onDoubleClick: onItemDoubleClick ? () => onItemDoubleClick(item) : undefined,
      ref: div => {
        if (div && selected) {
          this.ensureScrolledToView(div);
        }
      }
    }, itemRenderer({
      item: item,
      itemDisplayStringFunction: itemDisplayStringFunction,
      selected: selected,
      disabled: disabled,
      onItemClick: undefined,
      onItemDoubleClick: undefined
    }));
  }
}
//# sourceMappingURL=AbListBox.js.map
